import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Popconfirm, Popover } from 'antd';
import { Check, Users } from 'lucide-react';

/**
 * Chuyển nhanh giữa các tài khoản test, không cần nhập lại mật khẩu.
 * Mỗi tài khoản đăng nhập một lần bằng màn đăng nhập thường, phiên được nhớ trong trình duyệt này.
 * Chỉ bật trên đúng các host dưới đây, chỉ nhớ tài khoản *_test@khothi.vn, không nhớ Super Admin.
 */
const TEST_HOSTS = ['khothi-cms-v2.buso.asia', 'localhost', '127.0.0.1'];
const KEY = 'khothi-test-sessions-v1';

interface TestSession { email: string; name: string; token: string; user: string; userType: string; permissions: string }
type Sessions = Record<string, TestSession>;

export const isTestHost = (host: string) => TEST_HOSTS.includes(host);
export const isTestAccount = (email: unknown, type: unknown) =>
  typeof email === 'string' && /^[a-z0-9_.-]+_test@khothi\.vn$/i.test(email) && String(type) !== '1';

const read = (): Sessions => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
};

/** Phiên đang đăng nhập nếu là tài khoản test */
const currentSession = (): TestSession | undefined => {
  const token = localStorage.getItem('token');
  if (!token) return undefined;
  try {
    const raw = localStorage.getItem('user') || '{}';
    const u = JSON.parse(raw);
    const email = u?.email ?? u?.info?.email;
    const userType = localStorage.getItem('userType') || '';
    if (!isTestAccount(email, userType)) return undefined;
    return { email, name: u?.info?.fullname || u?.info?.name || u?.name || email, token, user: raw, userType, permissions: localStorage.getItem('permissions') || '[]' };
  } catch {
    return undefined;
  }
};

function Switcher() {
  const location = useLocation();
  const [sessions, setSessions] = useState<Sessions>(read);
  const [current, setCurrent] = useState<string>();

  const sync = useCallback(() => {
    const cur = currentSession();
    setCurrent(cur?.email);
    const all = read();
    if (cur) {
      all[cur.email] = cur;
      localStorage.setItem(KEY, JSON.stringify(all));
    }
    setSessions(all);
  }, []);

  useEffect(sync, [location.pathname, sync]);
  useEffect(() => {
    window.addEventListener('focus', sync);
    return () => window.removeEventListener('focus', sync);
  }, [sync]);

  const list = Object.values(sessions).sort((a, b) => a.email.localeCompare(b.email));
  if (!list.length) return null;

  const switchTo = (s: TestSession) => {
    localStorage.setItem('token', s.token);
    localStorage.setItem('user', s.user);
    localStorage.setItem('userType', s.userType);
    localStorage.setItem('permissions', s.permissions);
    // Tải lại trang để mọi store đọc lại tài khoản mới
    window.location.href = '/';
  };
  const clearAll = () => {
    localStorage.removeItem(KEY);
    setSessions({});
    sync();
  };

  return (
    <Popover
      trigger="click"
      placement="left"
      title="Chuyển tài khoản test"
      content={(
        <div className="w-[270px]">
          <div className="max-h-[320px] space-y-1 overflow-y-auto">
            {list.map((s) => (
              <button
                key={s.email} type="button" disabled={s.email === current} onClick={() => switchTo(s)} aria-label={`Chuyển sang ${s.email}`}
                className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5 text-left enabled:hover:border-[#3e91ab] enabled:hover:bg-[#3e91ab]/5 disabled:bg-gray-50 dark:border-gray-700 dark:disabled:bg-gray-800"
              >
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-[13px] font-medium text-gray-800 dark:text-gray-100">{s.name}</span>
                  <span className="block truncate text-[11px] text-gray-500">{s.email}</span>
                </span>
                {s.email === current && <Check size={15} className="shrink-0 text-[#3e91ab]" />}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] leading-snug text-gray-500">Thêm tài khoản: đăng xuất rồi đăng nhập tài khoản đó một lần. Phiên hết hạn thì đăng nhập lại.</p>
          <Popconfirm title="Xoá mọi phiên đã nhớ trên trình duyệt này?" okText="Xoá" cancelText="Không" onConfirm={clearAll}>
            <Button size="small" type="text" danger className="!mt-1 !px-1">Xoá phiên đã nhớ</Button>
          </Popconfirm>
        </div>
      )}
    >
      <button
        type="button" aria-label="Chuyển tài khoản test" title="Chuyển tài khoản test"
        className="fixed right-0 top-1/2 z-[1200] flex h-10 w-7 -translate-y-1/2 items-center justify-center rounded-l-lg bg-[#3e91ab] text-white shadow-md hover:w-8"
      >
        <Users size={15} />
      </button>
    </Popover>
  );
}

export default function TestSessionSwitcher() {
  return isTestHost(window.location.hostname) ? <Switcher /> : null;
}
